import React from "react";

const certs = ["FSSAI", "MSME", "Make in India", "ISO 22000", "HACCP", "HALAL", "KOSHER", "GMP", "APEDA"];
const partners = ["Nutra Co.", "Spice & Co", "FreshBlend", "Earth Pantry", "BioBakery", "Sun Powders", "Pure Pantry", "Olive & Oat"];

const Certifications = () => {
    return (
        <section
            data-testid="certifications-section"
            className="py-20 md:py-28 bg-sun-cream relative"
        >
            <div className="container-x">
                <div className="overline mb-6">— Compliance</div>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl mb-12 text-sun-ink">
                    Audited, licensed,
                    <br />
                    <span className="display-italic text-gradient"> documented.</span>
                </h2>

                <div className="grid grid-cols-3 md:grid-cols-9 gap-3 md:gap-4">
                    {certs.map((c) => (
                        <div
                            key={c}
                            data-testid={`cert-${c.toLowerCase().replace(/\s/g, "-")}`}
                            className="bg-white border border-sun-line rounded-full px-3 py-5 md:py-7 flex items-center justify-center text-center font-mono text-[10px] md:text-xs uppercase tracking-[0.18em] text-sun-ink hover:bg-sun-ink hover:text-sun-bg transition-colors duration-500"
                        >
                            {c}
                        </div>
                    ))}
                </div>

                {/* Govt-of-India trust badges strip */}
                <div
                    data-testid="govt-trust-strip"
                    className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4"
                >
                    {/* MSME / Udyam */}
                    <div className="bg-white border border-sun-line rounded-3xl p-5 md:p-6 flex items-center gap-4 hover:border-sun-orange transition-colors">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1F6FB2] to-[#0E4A7E] text-white flex items-center justify-center flex-shrink-0">
                            <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor">
                                <path d="M16 2L4 8v10c0 6 5.4 11 12 12 6.6-1 12-6 12-12V8L16 2zm0 4.5l8 4v6.7c0 4.3-3.6 7.9-8 8.8-4.4-.9-8-4.5-8-8.8V10.5l8-4z" />
                                <path d="M11 16l3.5 3.5L21 13" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sun-muted">
                                Govt. of India · Udyam
                            </div>
                            <div className="font-heading text-xl md:text-2xl text-sun-ink leading-tight">
                                MSME Registered
                            </div>
                            <div className="font-mono text-[10px] text-sun-inkSoft mt-1">
                                Recognised micro-enterprise
                            </div>
                        </div>
                    </div>

                    {/* FSSAI compact */}
                    <div className="bg-white border border-sun-line rounded-3xl p-5 md:p-6 flex items-center gap-4 hover:border-sun-orange transition-colors">
                        <div className="w-14 h-14 rounded-2xl bg-sun-ink text-white flex items-center justify-center flex-shrink-0 font-mono text-xs font-bold tracking-[0.16em]">
                            FSSAI
                        </div>
                        <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sun-muted">
                                Food Safety & Standards · India
                            </div>
                            <div className="font-heading text-xl md:text-2xl text-sun-ink leading-tight">
                                Licensed Facility
                            </div>
                            <div className="font-mono text-[10px] text-sun-inkSoft mt-1">
                                Lic. <span className="text-sun-ink font-semibold">10725997001012</span>
                            </div>
                        </div>
                    </div>

                    {/* Make in India */}
                    <div className="bg-white border border-sun-line rounded-3xl p-5 md:p-6 flex items-center gap-4 hover:border-sun-orange transition-colors">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sun-ink to-[#062236] text-sun-gold flex items-center justify-center flex-shrink-0">
                            {/* Stylised walking lion glyph */}
                            <svg viewBox="0 0 32 32" className="w-8 h-8" fill="currentColor">
                                <path d="M5 22c1.5-3 4-5 7-5 1.5 0 2.5.4 4 1l2-2c-.5-.7-1-1.7-1-2.8 0-1.6 1.2-2.7 2.8-2.7 1.4 0 2.5 1 2.7 2.3l1.5.4c-.2-2.3-2.2-4.1-4.6-4.1-2.7 0-4.7 2-4.7 4.6 0 .8.2 1.4.5 2L13 17.2c-1-.5-2.4-.8-3.5-.8C5.7 16.4 2.5 19 2 22h3zm12.5-.7c.7 1 1.7 1.7 2.8 1.7 1 0 2-.5 2.6-1.3l-1-.6c-.3.3-.7.4-1.1.4-.7 0-1.3-.5-1.5-1.1l-1.8.9zM26 9l-3 3 4 4-1 4 4-3 1-3-3-2-2-3z" />
                            </svg>
                        </div>
                        <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sun-muted">
                                Govt. of India · Initiative
                            </div>
                            <div className="font-heading text-xl md:text-2xl text-sun-ink leading-tight">
                                Made in India
                            </div>
                            <div className="font-mono text-[10px] text-sun-inkSoft mt-1">
                                Surat, Gujarat — proudly local
                            </div>
                        </div>
                    </div>
                </div>

                {/* FSSAI License — detailed band */}
                <div
                    data-testid="fssai-license-badge"
                    className="mt-6 bg-white border border-sun-line rounded-3xl p-6 md:p-8 flex items-start md:items-center gap-6"
                >
                    <div className="overline">— Verified manufacturer</div>
                    <div className="text-sm text-sun-inkSoft max-w-2xl leading-relaxed">
                        Every pouch carries our FSSAI license, batch code, MFG/EXP and a verifiable QR — full traceability, every time. Backed by MSME registration and proudly made in India.
                    </div>
                </div>

                <div className="mt-16 overflow-hidden border-y border-sun-line py-8 no-scrollbar">
                    <div className="marquee-track gap-16 items-center">
                        {[...partners, ...partners, ...partners].map((p, i) => (
                            <div
                                key={i}
                                className="font-heading display-italic text-3xl md:text-5xl text-sun-ink/30 hover:text-sun-orange transition-colors whitespace-nowrap px-8"
                            >
                                {p}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Certifications;
